import { useState } from 'react'
import { Settings, X, Minimize2, Maximize2 } from 'lucide-react'
import { QUANTUM_COLORS } from './constants.js'
import { getChartContext } from './context.js'

// Badge del dashboard: tarjeta paper con header arrastrable (molde
// patron_ruptura_quantum/Badge.jsx; la sección "Señal activa" sigue el estilo
// de supertrend_quantum/Badge.jsx). Toda lectura de `result` va con guardas:
// el badge nunca debe lanzar con result null/undefined/parcial.
//
// Secciones PLEGABLES (toggle +/−): el estado persiste en
// `config.panelSections.badge` (el reset lo preserva, igual que los
// acordeones del panel). El wrapper (DraggableRegistryBadge) pasa config +
// onChange desde v1.1; si no llegan (montaje sin config), el estado de los
// pliegues es local al componente.

function formatPrice(v) {
  if (!Number.isFinite(v)) return '—'
  if (Math.abs(v) >= 1000) return v.toFixed(1)
  if (Math.abs(v) >= 1) return v.toFixed(2)
  return v.toFixed(4)
}

function formatTime(t) {
  if (t == null) return '—'
  if (typeof t === 'number' && Number.isFinite(t)) {
    const d = new Date(t * 1000)
    return Number.isNaN(d.getTime()) ? String(t) : d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  }
  return String(t)
}

function formatSignedR(r) {
  if (!Number.isFinite(r)) return ''
  return `${r >= 0 ? '+' : ''}${r.toFixed(1)}R`
}

function outcomeLabel(sig) {
  if (!sig) return '—'
  if (sig.outcome === 'active') return sig.state === 'TP1' || sig.state === 'TP2' ? `Abierta (${sig.state}✓)` : 'Abierta'
  if (sig.outcome === 'win') return 'TP3 ✓'
  if (sig.outcome === 'be') return 'BE (TP1✓)'
  if (sig.outcome === 'loss') return 'SL ✗'
  // Invalidada: marca neutra con su R realizado ('Inv +0.8R' / 'Inv -0.3R').
  if (sig.outcome === 'invalidada') return `Inv ${formatSignedR(sig.realizedR)}`.trim()
  return '—'
}

// Progreso estilo STQ: qué niveles lleva tocados la señal (TP1✓ TP2✓ SL✗…).
function progressBadges(sig) {
  const badges = []
  if (sig?.tp1Hit != null) badges.push('TP1✓')
  if (sig?.tp2Hit != null) badges.push('TP2✓')
  if (sig?.tp3Hit != null) badges.push('TP3✓')
  if (sig?.outcome === 'loss') badges.push('SL✗')
  if (sig?.outcome === 'be') badges.push('BE')
  return badges
}

function gradeColor(grade) {
  return grade === 'A+' ? QUANTUM_COLORS.accent : QUANTUM_COLORS.neutral
}

function streakLabel(streak) {
  if (!streak || streak.type == null || !Number.isFinite(streak.count) || streak.count <= 0) return '—'
  return `${streak.count}${streak.type === 'win' ? 'W' : 'L'}`
}

function streakColor(streak) {
  if (!streak || streak.type == null) return 'var(--ink-soft, #4A5A52)'
  return streak.type === 'win' ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear
}

function Row({ label, value, color = 'var(--ink, #15211B)' }) {
  return (
    <div className="flex justify-between">
      <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>{label}</span>
      <span className="text-[9px] font-medium" style={{ color }}>{value}</span>
    </div>
  )
}

// Sección plegable del badge: plegada = solo el título con toggle +/−.
function BadgeSection({ title, open, onToggle, children }) {
  return (
    <div className="px-2.5 pb-1" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
      <button
        onClick={onToggle}
        className="w-full pt-1.5 pb-1 flex items-center justify-between"
        aria-expanded={open}
      >
        <span className="text-[10px] font-semibold" style={{ color: 'var(--ink, #15211B)' }}>{title}</span>
        <span className="text-[10px] font-medium" style={{ color: 'var(--ink-soft, #4A5A52)' }}>{open ? '−' : '+'}</span>
      </button>
      {open && children}
    </div>
  )
}

// Bloque de historial de un grado (A+ / A). Mismas métricas que el historial
// PRQ + racha y MAX DD (mejora propia, ver stats.js). `withBudget` es solo de
// display: el badge no usa la config para esto, se infiere de estimatedPnl.
function GradeBlock({ stats, withBudget }) {
  return (
    <div className="pb-1">
      <div className="pb-1 flex items-center justify-end">
        <span className="text-[9px] font-medium" style={{ color: 'var(--ink-soft, #4A5A52)' }}>
          {withBudget ? 'con budget' : 'sin budget (en R)'}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-x-2 gap-y-1">
        <Row label="Señales" value={stats.totalSignals} />
        <Row label="Winrate" value={`${((stats.winrate ?? 0) * 100).toFixed(0)}%`} />
        <Row label="TP1" value={`${((stats.tp1Rate ?? 0) * 100).toFixed(0)}%`} color={QUANTUM_COLORS.bull} />
        <Row label="TP2" value={`${((stats.tp2Rate ?? 0) * 100).toFixed(0)}%`} color={QUANTUM_COLORS.bull} />
        <Row label="TP3" value={`${((stats.tp3Rate ?? 0) * 100).toFixed(0)}%`} color={QUANTUM_COLORS.bull} />
        <Row label="SL" value={`${((stats.slRate ?? 0) * 100).toFixed(0)}%`} color={QUANTUM_COLORS.bear} />
        <Row label="Invalidadas" value={`${((stats.invalidatedRate ?? 0) * 100).toFixed(0)}%`} color={QUANTUM_COLORS.neutral} />
        <Row label="Abiertas" value={`${((stats.openRate ?? 0) * 100).toFixed(0)}%`} color={QUANTUM_COLORS.neutral} />
        <Row label="R medio" value={`${(stats.avgR ?? 0).toFixed(2)}R`} />
        <Row label="Racha" value={streakLabel(stats.streak)} color={streakColor(stats.streak)} />
        <Row label="MAX DD" value={`${(stats.maxDrawdown ?? 0).toFixed(2)}R`} color={(stats.maxDrawdown ?? 0) > 0 ? QUANTUM_COLORS.bear : 'var(--ink, #15211B)'} />
        {stats.estimatedPnl != null ? (
          <Row
            label="PnL est."
            value={`${stats.estimatedPnl >= 0 ? '+' : ''}${stats.estimatedPnl.toFixed(2)}`}
            color={stats.estimatedPnl >= 0 ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear}
          />
        ) : (stats.closedCount ?? 0) > 0 && (
          // Sin budget el PnL del historial se muestra igualmente, en R.
          <Row
            label="PnL"
            value={`${(stats.totalR ?? 0) >= 0 ? '+' : ''}${(stats.totalR ?? 0).toFixed(2)}R`}
            color={(stats.totalR ?? 0) >= 0 ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear}
          />
        )}
      </div>
    </div>
  )
}

// Defaults de pliegue: "Señal activa" desplegada; historiales y resto plegados.
const SECTION_DEFAULTS = Object.freeze({
  senalActiva: true,
  historialAp: false,
  historialA: false,
  combinado: false,
  ultimaSenal: false,
  ultimas10: false,
})

export default function ShowictQuantumBadge({
  result,
  config,
  onChange,
  onOpenSettings,
  onClose,
  onMinimize,
  minimized = false,
  version = '1.0.0',
  onHeaderMouseDown,
}) {
  const res = result && typeof result === 'object' ? result : null
  const signals = Array.isArray(res?.signals) ? res.signals : []
  const active = res?.activeSignal ?? null
  const last = res?.lastSignal ?? null
  const stats = res?.stats ?? null
  const gated = res?.gated === true || res == null

  // Estado de los pliegues: persiste en config.panelSections.badge si el
  // wrapper pasa config/onChange; si no, local al componente.
  const [localSections, setLocalSections] = useState({})
  const persistedBadge = config?.panelSections?.badge && typeof config.panelSections.badge === 'object'
    ? config.panelSections.badge
    : null
  const sections = persistedBadge ?? localSections
  const isOpen = (key) => sections[key] ?? SECTION_DEFAULTS[key] ?? false
  const toggleSection = (key) => {
    const next = { ...sections, [key]: !isOpen(key) }
    if (typeof onChange === 'function' && config && typeof config === 'object') {
      onChange({ panelSections: { ...(config.panelSections || {}), badge: next } })
    } else {
      setLocalSections(next)
    }
  }

  // Estado principal: señal viva > con señales recientes > sin señal.
  const stateLabel = active
    ? `${active.direction} ${active.grade ?? ''} activa`.replace(/\s+/g, ' ').trim()
    : signals.length > 0 && !gated
      ? 'Sin señal activa'
      : 'Sin señal'
  const stateColor = active
    ? (active.direction === 'LONG' ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear)
    : 'var(--ink-soft, #4A5A52)'

  // Distancia % al nivel más próximo (SL o TP1) desde la entrada.
  let nearestPct = null
  if (active && Number.isFinite(active.entry) && active.entry !== 0) {
    const dSl = Number.isFinite(active.sl) ? Math.abs(active.entry - active.sl) : Infinity
    const dTp1 = Number.isFinite(active.tp1) ? Math.abs(active.tp1 - active.entry) : Infinity
    const dMin = Math.min(dSl, dTp1)
    if (Number.isFinite(dMin)) nearestPct = (dMin / active.entry) * 100
  }

  const ctx = getChartContext() || {}
  const rowSymbol = typeof ctx.symbol === 'string' && ctx.symbol ? ctx.symbol : '—'
  const rowTf = typeof ctx.timeframe === 'string' && ctx.timeframe ? ctx.timeframe : '—'
  const last10 = Array.isArray(stats?.combined?.last10) ? [...stats.combined.last10].reverse() : []
  // La tabla de últimas 10 respeta el toggle del panel (viaja en el result:
  // el badge no usa la config para esto). Ausencia de la clave = visible.
  const showLastSignals = res?.showLastSignals !== false

  const ap = stats?.ap ?? null
  const a = stats?.a ?? null
  const combined = stats?.combined ?? null
  const showCombined = combined && combined.totalSignals > 0 && (ap?.totalSignals ?? 0) > 0 && (a?.totalSignals ?? 0) > 0

  return (
    <div
      className="absolute top-2 left-2 z-10 flex flex-col gap-1 rounded-lg backdrop-blur-sm shadow-lg overflow-hidden min-w-[220px] max-w-[280px]"
      style={{ background: 'var(--paper, #FDFDFA)', border: '1px solid var(--hair, rgba(176,141,87,0.35))' }}
    >
      <div
        className="flex items-center gap-1.5 px-2.5 pt-2 pb-1 cursor-grab active:cursor-grabbing"
        onMouseDown={onHeaderMouseDown}
      >
        <span
          className="text-[11px] font-semibold tracking-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif", color: 'var(--green-deep, #0B3D2E)' }}
        >
          SHOWICT Quantum <span className="opacity-60 font-normal">[{version}]</span>
          {minimized && (
            <span className="ml-1.5 text-[10px]" style={{ color: stateColor }}>· {stateLabel}</span>
          )}
        </span>
        <button onClick={onOpenSettings} title="Configuración" className="ml-auto p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
          <Settings size={12} />
        </button>
        {typeof onMinimize === 'function' && (minimized ? (
          <button onClick={onMinimize} title="Expandir" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <Maximize2 size={12} />
          </button>
        ) : (
          <button onClick={onMinimize} title="Minimizar" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <Minimize2 size={12} />
          </button>
        ))}
        <button onClick={onClose} title="Ocultar dashboard" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
          <X size={12} />
        </button>
      </div>

      {!minimized && (
        <>
          <div className="px-2.5 pb-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>Estado</span>
              <span className="text-[11px] font-semibold" style={{ color: stateColor }}>{stateLabel}</span>
            </div>
          </div>

          {active && (
            <BadgeSection title="Señal activa" open={isOpen('senalActiva')} onToggle={() => toggleSection('senalActiva')}>
              <div className="pb-1 flex items-center justify-between">
                <span className="text-[10px] font-medium" style={{ color: active.direction === 'LONG' ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear }}>
                  {active.direction} <span style={{ color: gradeColor(active.grade) }}>{active.grade ?? '—'}</span>{' '}
                  {progressBadges(active).join(' ') || 'Abierta'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-1 pb-1">
                <Row label="Dirección" value={active.direction} color={active.direction === 'LONG' ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear} />
                <Row label="Grado" value={active.grade ?? '—'} color={gradeColor(active.grade)} />
                <Row label="Entrada" value={formatPrice(active.entry)} />
                <Row label="Nivel próximo" value={nearestPct != null ? `${nearestPct.toFixed(2)}%` : '—'} />
                <Row label="Estado" value={outcomeLabel(active)} />
                <Row label="SL" value={formatPrice(active.sl)} color={QUANTUM_COLORS.bear} />
              </div>
            </BadgeSection>
          )}

          {ap && ap.totalSignals > 0 && (
            <BadgeSection title="Historial A+" open={isOpen('historialAp')} onToggle={() => toggleSection('historialAp')}>
              <GradeBlock stats={ap} withBudget={ap?.estimatedPnl != null} />
            </BadgeSection>
          )}

          {a && a.totalSignals > 0 && (
            <BadgeSection title="Historial A" open={isOpen('historialA')} onToggle={() => toggleSection('historialA')}>
              <GradeBlock stats={a} withBudget={a?.estimatedPnl != null} />
            </BadgeSection>
          )}

          {showCombined && (
            <BadgeSection title="Combinado" open={isOpen('combinado')} onToggle={() => toggleSection('combinado')}>
              <div className="grid grid-cols-2 gap-x-2 gap-y-1 pb-1">
                <Row label="Señales" value={combined.totalSignals} />
                <Row label="Racha" value={streakLabel(combined.streak)} color={streakColor(combined.streak)} />
                <Row label="MAX DD" value={`${(combined.maxDrawdown ?? 0).toFixed(2)}R`} color={(combined.maxDrawdown ?? 0) > 0 ? QUANTUM_COLORS.bear : 'var(--ink, #15211B)'} />
                {combined.estimatedPnl != null ? (
                  <Row
                    label="PnL total"
                    value={`${combined.estimatedPnl >= 0 ? '+' : ''}${combined.estimatedPnl.toFixed(2)}`}
                    color={combined.estimatedPnl >= 0 ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear}
                  />
                ) : (combined.closedCount ?? 0) > 0 && (
                  <Row
                    label="PnL total"
                    value={`${(combined.totalR ?? 0) >= 0 ? '+' : ''}${(combined.totalR ?? 0).toFixed(2)}R`}
                    color={(combined.totalR ?? 0) >= 0 ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear}
                  />
                )}
              </div>
            </BadgeSection>
          )}

          {last && (
            <BadgeSection title="Última señal" open={isOpen('ultimaSenal')} onToggle={() => toggleSection('ultimaSenal')}>
              <div className="grid grid-cols-2 gap-x-2 gap-y-1 pb-1">
                <Row label="Dirección" value={last.direction} color={last.direction === 'LONG' ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear} />
                <Row label="Grado" value={last.grade ?? '—'} color={gradeColor(last.grade)} />
                <Row label="Resultado" value={outcomeLabel(last)} />
                <Row label="Entrada" value={formatPrice(last.entry)} />
                <Row label="SL" value={formatPrice(last.sl)} />
                <Row label="TP1" value={formatPrice(last.tp1)} />
                <Row label="TP2" value={formatPrice(last.tp2)} />
                <Row label="TP3" value={formatPrice(last.tp3)} />
                <div className="flex justify-between col-span-2">
                  <span className="text-[9px]" style={{ color: 'var(--ink-soft, #4A5A52)' }}>Fecha</span>
                  <span className="text-[9px] font-medium" style={{ color: 'var(--ink, #15211B)' }}>{formatTime(last.time)}</span>
                </div>
              </div>
            </BadgeSection>
          )}

          {showLastSignals && last10.length > 0 && (
            <BadgeSection title={`Últimas ${last10.length}`} open={isOpen('ultimas10')} onToggle={() => toggleSection('ultimas10')}>
              {/* Filas compactas (fecha corta · dirección+grado · resultado)
                  con ellipsis: nunca se salen del marco del badge. */}
              <div className="flex flex-col gap-0.5 max-h-[160px] overflow-y-auto pb-1">
                {last10.map(sig => (
                  <div key={sig.id} className="flex items-center gap-1.5 min-w-0">
                    <span className="text-[8px] whitespace-nowrap shrink-0" style={{ color: 'var(--ink-soft, #4A5A52)' }}>
                      {formatTime(sig.time)} · {rowSymbol} · {rowTf}
                    </span>
                    <span className="text-[8px] font-medium whitespace-nowrap shrink-0" style={{ color: sig.direction === 'LONG' ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear }}>
                      {sig.direction} <span style={{ color: gradeColor(sig.grade) }}>{sig.grade ?? '—'}</span>
                    </span>
                    <span
                      className="text-[8px] font-medium min-w-0 flex-1 text-right overflow-hidden text-ellipsis whitespace-nowrap"
                      style={{ color: sig.outcome === 'win' || sig.outcome === 'be' ? QUANTUM_COLORS.bull : sig.outcome === 'loss' ? QUANTUM_COLORS.bear : QUANTUM_COLORS.neutral }}
                      title={`Entrada ${formatPrice(sig.entry)} · SL ${formatPrice(sig.sl)} · TP ${formatPrice(sig.tp1)}/${formatPrice(sig.tp2)}/${formatPrice(sig.tp3)}`}
                    >
                      {outcomeLabel(sig)}
                    </span>
                  </div>
                ))}
              </div>
            </BadgeSection>
          )}
        </>
      )}
    </div>
  )
}
